import * as React from 'react';

import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { useCustomerAccounts } from '../../../_graphql/queryCustomerAccounts';
import { $columnSelectedCustomerIds } from '../../../_state/loadsList.state';
import { useTranslations } from '../../../i18n/useTranslations';

interface Account {
  id: string;
  name: string;
  type?: string;
  email?: string;
  phone?: string;
  website?: string;
  relationship?: {
    isCustomer: boolean;
    isSupplier: boolean;
  };
}

interface CustomerColumnFilterProps {
  onClose?: () => void;
}

export const CustomerColumnFilter: React.FC<CustomerColumnFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedCustomerIdsSet, setSelectedCustomerIdsSet] = useAtom(
    $columnSelectedCustomerIds,
  );
  const selectedCustomerIds = Array.from(selectedCustomerIdsSet);
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  // Convert string array to Set for MultiSelectFilter
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedCustomerIds),
    [selectedCustomerIds],
  );

  // Fetch customers matching the search term
  const {
    data: customers,
    isFetching: isFetchingCustomers,
    isLoading: isLoadingCustomers,
  } = useCustomerAccounts({
    searchTerm,
    limit: 20,
  });

  // Fetch selected customers
  const { data: selectedCustomers } = useCustomerAccounts({
    accountIds: selectedCustomerIds,
  });

  // Handle search with debouncing via MultiSelectFilter
  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  // Handle selection changes
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedCustomerIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedCustomerIdsSet],
  );

  // Handle reset
  const handleReset = React.useCallback(() => {
    setSelectedCustomerIdsSet(new Set());
  }, [setSelectedCustomerIdsSet]);

  // Custom item renderer for customers
  const getLabel = React.useCallback(
    (item: Account, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.name}
        </div>
        {item.type && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs',
              item.type === 'Individual'
                ? 'bg-warning/10 text-warning'
                : 'bg-primary/10 text-primary',
            )}
          >
            {item.type}
          </span>
        )}
      </div>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@CustomerColumnFilter min-w-[350px]"
      entries={customers}
      selectedEntries={selectedCustomers}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(customer) => customer.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.filters.searchCustomers}
      isLoading={isLoadingCustomers}
      isFetching={isFetchingCustomers}
    />
  );
};
